import { Space } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import { MediaPlanFlatten, SortMediaPlansList } from '@skyrise-eng/types';
import { useGetMediaPlanListQuery, useGetUserListQuery, useGetClientListQuery } from '@/api';
import type {
  MediaPlanListColumns,
  UseMediaPlanList,
  ColumnType,
  HandleTableChange,
  HandleOnRow,
  MediaPlanListItem,
  MediaPlanListFilterValues
} from '../MediaPlanList.types';
import { resolveMediaPlanCurrency, resolveMediaPlanPoNumber } from '@/api/mediaPlanMappers';
import type { MediaPlanDetails } from '@/models';
import { COLUMNS, Column, STATUS_CLASS } from '../MediaPlanList.constants';
import { pipedriveDealUrl, toPascalCase } from '@/utils';
import { DEFAULT_CURRENCY, formatMoney } from '@/constants/currency';
import Actions from '../components/Actions/Actions';
import { DATE_FORMAT, PRIVATE_PATH } from '@/constants';
import { selectAuthState } from '@/selectors/auth';
import { Tag } from '@/components';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useModal } from '@/hooks/useModal';
import { usePagination } from '@/hooks/usePagination';
import { useSorting } from '@/hooks/useSorting';
import { useSearch } from '@/hooks/useSearch';

const { ChannelTag } = Tag;

const getCurrencyFormat = (value?: number, currency?: string | null): string =>
  value != null ? formatMoney(value, currency ?? DEFAULT_CURRENCY) : 'N/A';
const getPercentFormat = (value?: number): string =>
  value != null && Number.isFinite(Number(value)) ? `${value}%` : 'N/A';
const getDateFormat = (date?: Date): string => (date ? dayjs(date).format(DATE_FORMAT) : 'N/A');

export const useMediaPlanList: UseMediaPlanList = () => {
  const [mediaPlan, setMediaPlan] = useState<MediaPlanFlatten>();
  const { isOpen, close, open } = useModal();
  const { page, limit, handlePaginationChange, resetPage } = usePagination({ page: 1, limit: 10 });
  const { sort, order, handleSort } = useSorting<SortMediaPlansList>({ onSort: resetPage });
  const { search, handleSearch } = useSearch();
  const [filters, setFilters] = useState<Record<string, FilterValue | null>>();
  const [filterValues, setFilterValuesState] = useState<MediaPlanListFilterValues>({});
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuthState);
  const { data: users } = useGetUserListQuery({ page: 1 });
  const { data: clients } = useGetClientListQuery();

  // Used only to build the AM dropdown from "all" plans (not just current page).
  // This avoids the AM filter being limited to whatever is currently paginated into view.
  const { data: mediaPlansForOptions } = useGetMediaPlanListQuery(
    { page: 1, limit: 5000, order: -1 },
    { refetchOnMountOrArgChange: true }
  );
  const {
    data: mediaPlans,
    isLoading,
    isFetching
  } = useGetMediaPlanListQuery(
    ({
      // Search is applied client-side (deal ID, plan ID, campaign, client, AM) on the fetched slice.
      page: 1,
      limit: 5000,
      sort,
      order,
      ...filters,
      ...(filterValues.managerId ? { manager: [filterValues.managerId] } : {}),
      ...(filterValues.clientId ? { client: [filterValues.clientId] } : {})
      // NOTE: backend rejects startDateFrom/startDateTo/budgetMin/budgetMax/status today,
      // so we apply those client-side below.
    } as any),
    { refetchOnMountOrArgChange: true }
  );

  const setFilterValues = useCallback((next: Partial<MediaPlanListFilterValues>) => {
    setFilterValuesState((prev) => ({ ...prev, ...next }));
    resetPage();
  }, [resetPage]);

  const resetAllFilters = useCallback(() => {
    setFilterValuesState({});
    setFilters(undefined);
    handleSearch('');
    resetPage();
  }, [handleSearch, resetPage]);

  const statusOptions = useMemo(
    () => [
      { label: 'Draft', value: 'DRAFT' },
      { label: 'Ready', value: 'READY' },
      { label: 'Sent', value: 'SENT' }
    ],
    []
  );

  const amOptions = useMemo(() => {
    const list = mediaPlansForOptions?.data ?? [];
    if (!list.length) return [];

    // Only show AMs that actually appear on at least 1 plan.
    const map = new Map<string, string>();
    for (const p of list) {
      const id = (p as any)?.manager?._id as string | undefined;
      if (!id) continue;
      const nameFromPlan = [p.manager?.firstName, p.manager?.lastName].filter(Boolean).join(' ').trim();
      map.set(id, nameFromPlan || map.get(id) || id);
    }

    // If we have the user list, prefer its labels (more consistent).
    for (const u of users?.data ?? []) {
      if (!map.has(u._id)) continue;
      map.set(u._id, [u.firstName, u.lastName].join(' ').trim());
    }

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .filter((o) => o.label && o.label !== o.value)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [mediaPlansForOptions?.data, users?.data]);

  const clientOptions = useMemo(
    () => {
      const list = mediaPlansForOptions?.data ?? [];
      if (!list.length) return [];

      // Only show clients that actually appear on at least 1 plan.
      const map = new Map<string, string>();
      for (const p of list) {
        const id = (p as any)?.client?._id as string | undefined;
        if (!id) continue;
        const nameFromPlan = (p as any)?.client?.name as string | undefined;
        map.set(id, (nameFromPlan || map.get(id) || id).trim());
      }

      // If we have the client list, prefer its labels (more consistent).
      for (const c of clients ?? []) {
        if (!map.has(c._id)) continue;
        map.set(c._id, c.name?.trim() || map.get(c._id) || c._id);
      }

      return Array.from(map.entries())
        .map(([value, label]) => ({ value, label }))
        .filter((o) => o.label && o.label !== o.value)
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    [mediaPlansForOptions?.data, clients]
  );

  const dealIdOptions = useMemo(() => {
    const list = mediaPlansForOptions?.data ?? [];
    if (!list.length) return [];

    const set = new Set<string>();
    for (const p of list) {
      const id = String((p as any)?.campaign?.campaignId ?? '').trim();
      if (!id) continue;
      set.add(id);
    }

    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [mediaPlansForOptions?.data]);

  const filteredPlans = useMemo(() => {
    const list = mediaPlans?.data ?? [];
    if (!list.length) return list;

    const term = search?.trim().toLowerCase();
    const status = filterValues.status;
    const selectedDealIds = filterValues.dealIds ?? [];
    const from = filterValues.startDateFrom ? dayjs(filterValues.startDateFrom) : null;
    const to = filterValues.startDateTo ? dayjs(filterValues.startDateTo) : null;
    const min = typeof filterValues.budgetMin === 'number' ? filterValues.budgetMin : null;
    const max = typeof filterValues.budgetMax === 'number' ? filterValues.budgetMax : null;

    return list.filter((p) => {
      if (term) {
        const campaignName = String(p.campaign?.name ?? '').toLowerCase();
        const dealId = String((p.campaign as { campaignId?: string | number })?.campaignId ?? '');
        const clientName = String((p.client as { name?: string })?.name ?? '').toLowerCase();
        const managerName = [p.manager?.firstName, p.manager?.lastName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        const planId = String(p._id ?? '').toLowerCase();
        const matchesSearch =
          campaignName.includes(term) ||
          dealId.toLowerCase().includes(term) ||
          clientName.includes(term) ||
          managerName.includes(term) ||
          planId.includes(term);
        if (!matchesSearch) return false;
      }

      if (status && String(p.status).toUpperCase() !== status) return false;
      if (selectedDealIds.length) {
        const campaignId = String((p as any)?.campaign?.campaignId ?? '').trim();
        if (!selectedDealIds.includes(campaignId)) return false;
      }

      if (from || to) {
        const start = p.startDate ? dayjs(p.startDate) : null;
        if (start) {
          if (from && start.isBefore(from, 'day')) return false;
          if (to && start.isAfter(to, 'day')) return false;
        }
      }

      const budget = typeof p.totalBudget === 'number' ? p.totalBudget : null;
      if (min != null && budget != null && budget < min) return false;
      if (max != null && budget != null && budget > max) return false;

      return true;
    });
  }, [
    mediaPlans?.data,
    search,
    filterValues.status,
    filterValues.dealIds,
    filterValues.startDateFrom,
    filterValues.startDateTo,
    filterValues.budgetMin,
    filterValues.budgetMax
  ]);

  const paginatedFilteredPlans = useMemo(() => {
    const start = (page - 1) * limit;
    const end = start + limit;
    return filteredPlans.slice(start, end);
  }, [filteredPlans, page, limit]);

  const mediaPlanList: MediaPlanListItem[] | [] = useMemo(() => {
    return paginatedFilteredPlans.map((plan) => {
      const { _id, startDate, endDate, totalBudget, ...rest } = plan;
      const currency = resolveMediaPlanCurrency(plan as MediaPlanDetails);

      return {
        _id,
        key: _id,
        startDate: getDateFormat(startDate),
        endDate: getDateFormat(endDate),
        totalBudget: getCurrencyFormat(totalBudget, currency),
        ...rest
      };
    });
  }, [paginatedFilteredPlans]);

  const handleEditMediaPlan = useCallback(
    (id: string) => {
      const mediaPlanToEdit = mediaPlans?.data.find(({ _id }) => _id === id);

      if (mediaPlanToEdit) {
        setMediaPlan(mediaPlanToEdit);
        open();
      }
    },
    [mediaPlans?.data, open]
  );

  const columns = useMemo((): MediaPlanListColumns => {
    COLUMNS[Column.CAMPAIGN].render = (_, { campaign }) => campaign.name;
    /* The deal id opens its PipeDrive deal, in a new tab. The click is kept
       off the row, which would otherwise also open the plan. */
    COLUMNS[Column.DEAL_ID].render = (_, { campaign }) => {
      const dealId = (campaign as any)?.campaignId;
      const url = pipedriveDealUrl(dealId);
      if (!url) return dealId ?? 'N/A';
      return (
        <a href={url} target="_blank" rel="noreferrer noopener" onClick={(event) => event.stopPropagation()}>
          {dealId}
        </a>
      );
    };
    COLUMNS[Column.MANAGER].filters = users?.data.map(({ firstName, lastName, _id }) => ({
      text: [firstName, lastName].join(' '),
      value: _id
    }));
    /* Guarded, like the client column below it. A plan whose manager no longer
       resolves to a user now reaches this list rather than being dropped from
       it, and reading through an absent manager threw, taking the whole page
       down with it — one unresolvable plan made every plan unviewable. */
    COLUMNS[Column.MANAGER].render = (_, { manager }) =>
      [manager?.firstName, manager?.lastName].filter(Boolean).join(' ') || '—';
    COLUMNS[Column.CLIENT].filters = clients?.map(({ name, _id }) => ({
      text: name,
      value: _id
    }));
    COLUMNS[Column.CLIENT].render = (_, { client }) => client?.name;
    COLUMNS[Column.STATUS].render = (_, { status }) => (
      <Space className={`status ${STATUS_CLASS[status]}`}>{toPascalCase(status)}</Space>
    );
    COLUMNS[Column.EXPECTED_MARGIN].render = (_, { marginPercent }) => getPercentFormat(marginPercent);
    COLUMNS[Column.CHANNELS].render = (_, { channels }) => (
      <Space size={5} wrap>
        {channels?.map((channel) => <ChannelTag key={channel}>{channel}</ChannelTag>) || 'N/A'}
      </Space>
    );
    COLUMNS[Column.ACTIONS].render = (_, row) => (
      <Actions
        campaignName={row.campaign.name}
        id={row._id}
        status={row.status}
        edit={handleEditMediaPlan}
        poNumber={resolveMediaPlanPoNumber(row)}
      />
    );

    return Object.values(COLUMNS).map(
      ({ i18nKey, ...rest }): ColumnType<MediaPlanListItem> => ({ title: t(i18nKey), ...rest })
    );
  }, [users, clients, handleEditMediaPlan, t]);

  const handleTableChange: HandleTableChange = (_, filtersMap, sorter) => {
    if (!Array.isArray(sorter)) {
      handleSort(sorter as unknown as SorterResult<SortMediaPlansList>);
    }

    setFilters(filtersMap);
  };

  const handleOnRow: HandleOnRow = (row) => ({
    onClick: () => navigate(`${PRIVATE_PATH.MEDIA_PLAN}/${row._id}`)
  });

  const handleFormClose = () => {
    setMediaPlan(undefined);
    close();
  };

  return {
    filters,
    mediaPlan,
    isFormOpen: isOpen,
    handleFormOpen: open,
    handleFormClose,
    columns,
    limit,
    page,
    handlePaginationChange,
    handleTableChange,
    handleOnRow,
    isLoading,
    isFetching,
    data: mediaPlanList,
    count: filteredPlans.length,
    userRole: user?.role,
    search,
    handleSearch,
    resetAllFilters,
    amOptions,
    clientOptions,
    dealIdOptions,
    statusOptions,
    filterValues,
    setFilterValues
  };
};
